import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { useEffect } from 'react';
import { useFishDetails } from '../FishDetailsContext';
import { FaPlus } from 'react-icons/fa';
import { Card } from '@Components/Card/index';
import FishCard from '@Components/FishCard/index';
import LoadingSpinner from '@Components/LoadingSpinner';
import ReferralList from '@Components/ReferralList';
import ShowFor from '@Components/ShowFor';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Select as AccessSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';

const PAYMENT_ACCESS_OPTIONS = [
  { value: 'OWN', label: 'Own' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'BOTH', label: 'Both' },
];

const PAYMENT_ACCESS_FIELDS = [
  { field: 'cardAccess', label: 'Card access' },
  { field: 'walletAccess', label: 'Wallet access' },
];

/** Card and wallet access selects for one shared-subscription link. */
function PaymentAccessSelects({ link, onChange }) {
  return (
    <div className="flex gap-4">
      {PAYMENT_ACCESS_FIELDS.map(({ field, label }) => (
        <div key={field} className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground font-bold">{label}</span>
          <AccessSelect
            value={link?.[field]}
            onValueChange={(value) => onChange({ [field]: value })}
          >
            <SelectTrigger className="w-32" aria-label={label}>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_ACCESS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </AccessSelect>
        </div>
      ))}
    </div>
  );
}

export default function MembershipPanel() {
  const {
    handleTabsValueChange,
    activeSubscription,
    actor,
    addDialogOpen,
    deleteDialogOpen,
    deleteNameForSharedSub,
    displaySubscriptionStatus,
    fishOptions,
    formatDisplayNameForFishCard,
    getInitialWalletPromoCredit,
    getQuarterlyMatchAmount,
    handleUpdateSharedSubscription,
    handleUpdateSharedSubscriptionAccess,
    id,
    isLoading,
    isSearching,
    otherFishOnSubscription,
    paymentSources,
    selectedFishSubToAdd,
    setAddDialogOpen,
    setDeleteDialogOpen,
    setDeleteNameForSharedSub,
    setFishCardIndex,
    setIsCancelSubOpen,
    setSelectedFishSubToAdd,
    setSharedSubInputValue,
    setSharedSubSearchTerm,
    sharedSubInputValue,
    sharedSubTimeoutRef,
    sharedTabMessage,
    status,
    walletBalance,
  } = useFishDetails();

  const parentWallet =
    paymentSources?.wallets.find((wallet) => wallet.owner === 'PARENT') ??
    null;

  // Shared-subscription members are fetched on demand rather than with the fish.
  useEffect(() => {
    if (actor?.id && actor?.subscriptions?.length) {
      handleTabsValueChange('connections');
    }
  }, [actor?.id]);

  return (
    <>
                  <ShowFor permissions={['MEMBERS_WRITE']}>
                    <Card className="px-4">
                      <div className="mx-auto flex flex-row">
                        <div>
                          <Heading level={'h6'}>Subscription Status:</Heading>
                          <Separator className="bg-aoh-accent dark:bg-[#caab6a]" />
                        </div>
  
                        <div>
                          <div className="px-4">
                            {displaySubscriptionStatus?.status || ''}
                          </div>
                        </div>
                      </div>
  
                      {displaySubscriptionStatus?.status === 'Active' && (
                        <Button
                          onClick={() => {
                            setIsCancelSubOpen(true);
                          }}
                          size={'lg'}
                          className="mx-auto w-1/2"
                          data-testid={
                            'member-details-cancel-subscription-button'
                          }
                        >
                          Cancel member's subscription
                        </Button>
                      )}
                    </Card>
                  </ShowFor>
  
                  <Card>
                    <div className="flex gap-12 px-4 text-sm">
                      <div className="flex flex-col">
                        <div className="pt-4 text-lg font-bold text-aoh-ink underline dark:text-zinc-100">
                          Subscription:
                        </div>
                        <div className="text-muted-foreground pt-4 font-bold">
                          Subscription Title
                        </div>
                        <div>
                          {actor?.sharedSubscription?.length > 0
                            ? '[Shared]'
                            : ''}{' '}
                          {activeSubscription?.subscriptionTier?.title
                            ? activeSubscription?.subscriptionTier?.title
                            : 'N/A'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Subscription Start Date
                        </div>
                        <div>
                          {DateTime.fromISO(activeSubscription?.beginsAt)
                            .toLocal()
                            .toFormat('MMM dd, yyyy')}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Last Subscription Renewal Date
                        </div>
                        <div>
                          {activeSubscription?.subscriptionRenewedAt
                            ? DateTime.fromISO(
                                activeSubscription?.subscriptionRenewedAt,
                              )
                                .toLocal()
                                .toFormat('MMM dd, yyyy')
                            : 'Not Renewed Yet'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Next Subscription Renewal
                        </div>
                        <div>
                          {DateTime.fromISO(
                            activeSubscription?.nextSubscriptionRenewalDate,
                          )
                            .toLocal()
                            .toFormat('MMMM dd, yyyy')}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Subscription Description
                        </div>
                        <div>
                          {activeSubscription?.subscriptionTier?.description ||
                            'N/A'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Discount Code Used:
                        </div>
                        <div>{actor?.discountInfo?.code || 'N/A'}</div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Annual Membership Fee Before Promotional Discount:
                        </div>
                        <div>
                          {Number(
                            activeSubscription?.initialSubscriptionPrice,
                          )?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Annual Membership Fee After Promotional Discount:
                        </div>
                        <div>
                          {actor?.discountInfo?.subscriptionPriceAfterDiscount
                            ? actor?.discountInfo?.subscriptionPriceAfterDiscount
                                .toNumber()
                                .toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                })
                            : 'N/A (no discount code used)'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Subscription Promotional Discount Still Valid?
                        </div>
  
                        <div>
                          {typeof actor?.discountInfo
                            ?.subscriptionDiscountHasEnded === 'boolean'
                            ? actor.discountInfo.subscriptionDiscountHasEnded
                              ? `Discount ended at ${DateTime.fromISO(actor.discountInfo.subscriptionDiscountEndsAt).toLocal().toFormat('MMMM dd, yyyy')}`
                              : `Yes, discount ends at ${DateTime.fromISO(actor.discountInfo.subscriptionDiscountEndsAt).toLocal().toFormat('MMMM dd, yyyy')}`
                            : 'N/A (no discount code used)'}
                        </div>
  
                        <div className="pt-4 text-lg font-bold text-aoh-ink underline dark:text-zinc-100">
                          Referral:
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Referred By
                        </div>
                        <div>
                          {activeSubscription?.referralId
                            ? `${activeSubscription?.referral?.fish?.memberProfile?.fName} ${activeSubscription?.referral?.fish?.memberProfile?.lName}`
                            : 'N/A'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Referral Code
                        </div>
                        <div>
                          {activeSubscription?.referralId
                            ? `${activeSubscription?.referral?.registrationCode?.code}`
                            : 'N/A'}
                        </div>
                      </div>
  
                      <div className="flex flex-col">
                        <div className="pt-4 text-lg font-bold text-aoh-ink underline dark:text-zinc-100">
                          Wallet:
                        </div>
                        <div className="text-muted-foreground pt-4 font-bold">
                          Wallet Renewal Price Before Promotional Discount:
                        </div>
                        <div>
                          {Number(
                            activeSubscription?.initialWalletPrice,
                          ).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Wallet Renewal Price After Promotional Discount:
                        </div>
                        <div>
                          {actor?.discountInfo?.walletPriceAfterDiscount
                            ? actor?.discountInfo?.walletPriceAfterDiscount
                                ?.toNumber()
                                ?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                })
                            : 'N/A (no discount code used)'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Wallet Renewal Frequency:
                        </div>
                        <div>
                          {activeSubscription?.walletDuration &&
                          activeSubscription?.walletUnit
                            ? `Renews every ${activeSubscription?.walletDuration} ${activeSubscription?.walletUnit?.toLowerCase()}s`
                            : 'N/A (no wallet spend associated with subscription)'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Current Wallet Balance:
                        </div>
                        <div>
                          {walletBalance !== null
                            ? `$${Number(walletBalance)?.toFixed(2)}`
                            : 'N/A'}
                        </div>
                        {parentWallet && (
                          <>
                            <div className="text-muted-foreground pt-4 font-bold">
                              {parentWallet.ownerName}'s Wallet Balance (Shared):
                            </div>
                            <div>{`$${Number(parentWallet.balance).toFixed(2)}`}</div>
                          </>
                        )}
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Next Wallet Renewal
                        </div>
                        <div>
                          {actor?.wallet?.nextWalletRenewal
                            ? DateTime.fromISO(actor?.wallet?.nextWalletRenewal)
                                .toLocal()
                                .toFormat('MMMM dd, yyyy')
                            : 'N/A'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Wallet Promotion Still Valid?
                        </div>
                        <div>
                          {typeof actor?.discountInfo?.walletDiscountHasEnded ===
                          'boolean'
                            ? actor.discountInfo.walletDiscountHasEnded
                              ? `Discount ended at ${DateTime.fromISO(actor.discountInfo.walletDiscountEndsAt).toLocal().toFormat('MMMM dd, yyyy')}`
                              : `Yes, discount ends at ${DateTime.fromISO(actor.discountInfo.walletDiscountEndsAt).toLocal().toFormat('MMMM dd, yyyy')}`
                            : 'N/A (no discount code used)'}
                        </div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Initial Wallet Promotional Credit:
                        </div>
                        <div>{getInitialWalletPromoCredit()}</div>
  
                        <div className="text-muted-foreground pt-4 font-bold">
                          Quarterly Wallet Match:
                        </div>
                        <div>
                          {actor?.discountInfo?.code?.trim()?.toUpperCase() ===
                          'ROMA'
                            ? 'N/A'
                            : getQuarterlyMatchAmount()}
                        </div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-8">
                    <div className="flex w-full flex-col gap-4">
                      <h2>Wallet Overview</h2>
  
                      <div>
                        Pairing Code:{' '}
                        <span className="font-semibold">
                          {actor?.pairingCode ? actor.pairingCode : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </Card>
                  {isLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <div>
                      {/* Shared Subscriptions */}
                      <div>
                        {actor?.sharedSubscription?.length <= 0 ||
                        actor?.sharedSubscription[0]?.subscription?.fishId ===
                          actor?.id ? (
                          <>
                            <div className="align-center flex justify-between gap-2 pt-4">
                              <div className="flex min-w-3/4 flex-col">
                                <div>
                                  <label className="text-xl font-bold">
                                    Shared Accounts
                                  </label>
                                  <div className="py-2 text-xs text-[#6B6B6B]">
                                    Select other users to add or remove from this
                                    member's subscription
                                  </div>
                                </div>
  
                                {sharedTabMessage && (
                                  <div className="pt-4">{sharedTabMessage}</div>
                                )}
                              </div>
  
                              <ShowFor permissions={['MEMBERS_WRITE']}>
                                <div className="flex flex-col gap-2 pt-6">
                                  <div className="flex">
                                    <Button
                                      className="w-full font-bold"
                                      onClick={() => setAddDialogOpen(true)}
                                    >
                                      <FaPlus />
                                      Link account
                                    </Button>
                                  </div>
                                </div>
                              </ShowFor>
                            </div>
  
                            <div className="space-y-2 pt-4">
                              {otherFishOnSubscription?.map(
                                (subscription, index) => (
                                  <div
                                    key={`fish-card-index-${index}`}
                                    className="flex flex-col gap-2"
                                  >
                                    <FishCard
                                      index={index}
                                      setFishCardIndex={setFishCardIndex}
                                      subscription={subscription}
                                      fish={actor}
                                      displayName={formatDisplayNameForFishCard(
                                        subscription.fish,
                                      )}
                                      setDeleteNameForSharedSub={
                                        setDeleteNameForSharedSub
                                      }
                                      cloudinaryId={
                                        subscription?.fish?.profileImageId ||
                                        null
                                      }
                                      deleteDialogOpen={deleteDialogOpen}
                                      setDeleteDialogOpen={setDeleteDialogOpen}
                                      buttonText="Remove"
                                      buttonVariant="destructive"
                                      showDeleteButton={true}
                                    />
                                    <ShowFor permissions={['MEMBERS_WRITE']}>
                                      <PaymentAccessSelects
                                        link={subscription}
                                        onChange={(access) =>
                                          handleUpdateSharedSubscriptionAccess(
                                            subscription.fishId,
                                            access,
                                          )
                                        }
                                      />
                                    </ShowFor>
                                  </div>
                                ),
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="p-4">
                            <h2 className="text-red-500">
                              *Customers already on another member's subscription
                              cannot update the shared subscription
                            </h2>
                            <ShowFor permissions={['MEMBERS_WRITE']}>
                              <div className="pt-4">
                                <div className="pb-2 font-bold">
                                  Payment access on the shared subscription
                                </div>
                                <PaymentAccessSelects
                                  link={actor?.sharedSubscription?.[0]}
                                  onChange={(access) =>
                                    handleUpdateSharedSubscriptionAccess(
                                      actor.id,
                                      access,
                                    )
                                  }
                                />
                              </div>
                            </ShowFor>
                          </div>
                        )}
                      </div>
  
                      <hr className="my-6 border-aoh-border dark:border-zinc-700" />
  
                      {/* Referrals */}
                      <div>
                        <label className="text-xl font-bold">Referrals</label>
                        <ReferralList memberId={id} />
                      </div>
                    </div>
                  )}
  
                  <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogContent className="bg-[#FFFFFF]">
                      <DialogHeader>
                        <DialogTitle className="pb-2 text-2xl">
                          Link Accounts
                        </DialogTitle>
                        <DialogDescription>
                          <div>
                            Select an account to link to
                            <span className="font-semibold text-zinc-800">
                              {' ' + formatDisplayNameForFishCard(actor) + `'s `}
                            </span>
                            subscription
                          </div>
                          <div>
                            <Select
                              value={selectedFishSubToAdd}
                              isLoading={isSearching && status === 'pending'}
                              options={fishOptions}
                              filterOption={() => true}
                              onChange={(option) => {
                                setSelectedFishSubToAdd(option);
                              }}
                              inputValue={sharedSubInputValue}
                              onInputChange={(inputValue, { action }) => {
                                if (action !== 'input-change') return;
  
                                const digits = inputValue
                                  .replace(/\D/g, '')
                                  .slice(0, 10);
                                setSharedSubInputValue(digits);
  
                                window.clearTimeout(sharedSubTimeoutRef.current);
                                sharedSubTimeoutRef.current = window.setTimeout(() => {
                                  setSharedSubSearchTerm(digits);
                                }, 350);
                              }}
                              className="basic-single py-4"
                              classNamePrefix="select"
                              placeholder="Enter phone number..."
                              isSearchable={true}
                            />
                          </div>
                          {!selectedFishSubToAdd ? (
                            <div className="flex h-20 w-full flex-col justify-center bg-zinc-100 p-4 text-center text-zinc-500">
                              Select an account
                            </div>
                          ) : (
                            <FishCard
                              fish={selectedFishSubToAdd}
                              displayName={formatDisplayNameForFishCard(
                                selectedFishSubToAdd,
                              )}
                              setDeleteNameForSharedSub={
                                setDeleteNameForSharedSub
                              }
                              deleteDialogOpen={deleteDialogOpen}
                              setDeleteDialogOpen={setDeleteDialogOpen}
                              buttonText={'Remove'}
                              buttonVariant={'destructive'}
                            />
                          )}
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button
                            onClick={() => {
                              setSelectedFishSubToAdd(null);
                              setAddDialogOpen(false);
                            }}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button
                          onClick={() => {
                            handleUpdateSharedSubscription(false);
                          }}
                        >
                          Link accounts
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
  
                  <Dialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                  >
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="pb-4 text-2xl">
                          Remove customer from subscription?
                        </DialogTitle>
                        <DialogDescription>
                          <span className="font-semibold text-zinc-800">
                            {deleteNameForSharedSub + ' '}
                          </span>
                          will be removed from the shared subscription
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button
                            onClick={() => {
                              setDeleteDialogOpen(false);
                            }}
                            variant="outline"
                          >
                            Close
                          </Button>
                        </DialogClose>
                        <Button
                          onClick={() => {
                            handleUpdateSharedSubscription(true);
                          }}
                        >
                          Confirm
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
    </>
  );
}
